import { useEffect, useMemo, useState } from "react";
import {
  Download,
  Eye,
  FileText,
  Globe,
  Loader2,
  Mail,
  MessageCircle,
  Radar,
  RefreshCw,
  Search,
  Send,
  Trash2,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import WhatsAppSequence from "@/components/WhatsAppSequence";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SCORE_STYLE = {
  HOT: "bg-red-100 text-red-700 border-red-200",
  WARM: "bg-amber-100 text-amber-700 border-amber-200",
  COLD: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATO_LABEL = {
  nuovo: "Nuovo",
  da_contattare: "In coda",
  contattato: "Contattato",
  risposto: "Ha risposto",
  chiuso: "Chiuso",
  scartato: "Scartato",
};

const Select = ({ id, value, onChange, children }) => (
  <select
    id={id}
    data-testid={id}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
  >
    {children}
  </select>
);

const LeadFinder = () => {
  const [meta, setMeta] = useState(null);
  const [regione, setRegione] = useState("Lombardia");
  const [ambito, setAmbito] = useState("regione");
  const [luogo, setLuogo] = useState("");
  const [categoria, setCategoria] = useState("serramenti");
  const [searching, setSearching] = useState(false);
  const [jobMsg, setJobMsg] = useState("");
  const [soloContatti, setSoloContatti] = useState(true);
  const [busy, setBusy] = useState("");
  const [filters, setFilters] = useState({
    regione: "",
    categoria: "",
    score: "",
    stato: "",
    whatsapp: "",
    contatto: "",
    q: "",
  });
  const [data, setData] = useState({ items: [], total: 0, stats: {} });
  const [selected, setSelected] = useState([]);
  const [queue, setQueue] = useState(null);
  const [followups, setFollowups] = useState({ items: [], total: 0, days: 3 });
  const [sequence, setSequence] = useState(null);
  const [recent, setRecent] = useState({ items: [], total: 0 });

  const loadRecent = () =>
    api.get("/leads/recent-contacts").then(({ data }) => setRecent(data)).catch(() => {});

  const loadFollowups = () =>
    api.get("/leads/followups").then(({ data }) => setFollowups(data)).catch(() => {});

  const loadMeta = () => api.get("/leads/meta").then(({ data }) => setMeta(data));
  const loadQueue = () => api.get("/leads/email-queue").then(({ data }) => setQueue(data));

  const load = () =>
    api
      .get("/leads", { params: { ...filters, limit: 300 } })
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(apiError(err.response?.data?.detail)));

  useEffect(() => {
    loadMeta();
    loadQueue();
    loadFollowups();
    loadRecent();
  }, []);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const catLabel = useMemo(() => {
    const map = {};
    (meta?.categorie || []).forEach((c) => (map[c.key] = c.label));
    return map;
  }, [meta]);

  const cerca = async () => {
    setSearching(true);
    setJobMsg("Ricerca avviata su OpenStreetMap…");
    try {
      const { data } = await api.post("/leads/find", {
        regione: ambito === "regione" ? regione : luogo.trim(),
        ambito,
        categoria,
        limit: 300,
        solo_contatti: soloContatti,
      });
      pollJob(data.job_id);
    } catch (err) {
      setSearching(false);
      setJobMsg("");
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const pollJob = (jobId, kind = "find") => {
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const { data: job } = await api.get(`/leads/jobs/${jobId}`);
        if (job.status === "running") {
          setJobMsg(
            job.note ||
              (kind === "enrich"
                ? `Lettura siti in corso… (${tries * 3}s)`
                : `Ricerca in corso su ${job.regione}… (${tries * 3}s)`)
          );
          if (tries > 220) {
            clearInterval(timer);
            setSearching(false);
            setJobMsg("");
            toast.error("Operazione troppo lunga, riprova tra qualche minuto");
          }
          return;
        }
        clearInterval(timer);
        setSearching(false);
        setJobMsg("");
        if (job.status === "failed") {
          toast.error(`Non riuscita: ${job.error || "errore sconosciuto"}`);
        } else if (kind === "enrich") {
          toast.success(`Siti letti: ${job.found} · email trovate: ${job.saved}`);
        } else {
          toast.success(
            `Trovate ${job.found} aziende · ${job.saved} nuove salvate · ${job.duplicates} duplicati · ${job.skipped || 0} senza contatti`
          );
        }
        await load();
        await loadMeta();
      } catch {
        clearInterval(timer);
        setSearching(false);
        setJobMsg("");
      }
    }, 3000);
  };

  const bulk = async (action) => {
    if (!selected.length) return toast.error("Seleziona almeno un lead");
    setBusy(action);
    try {
      if (action === "enrich") {
        const { data } = await api.post("/leads/enrich", { ids: selected });
        setJobMsg("Sto leggendo i siti per trovare le email…");
        setSearching(true);
        pollJob(data.job_id, "enrich");
      }
      if (action === "whatsapp") {
        const { data } = await api.post("/leads/check-whatsapp", { ids: selected });
        toast.success(`${data.mobile} cellulari su ${data.checked} lead`);
      }
      if (action === "queue") {
        const { data } = await api.post("/leads/queue-email", { ids: selected });
        const m = data.motivi || {};
        const perche = [
          m.senza_email && `${m.senza_email} senza indirizzo email`,
          m.gia_in_coda && `${m.gia_in_coda} già in coda`,
          m.gia_inviata && `${m.gia_inviata} già contattati per email`,
          m.disiscritto && `${m.disiscritto} disiscritti`,
        ]
          .filter(Boolean)
          .join(" · ");
        if (data.queued === 0) {
          toast.error(
            `Nessuna email messa in coda. Motivo: ${perche || "nessun lead valido"}`,
            { duration: 8000 }
          );
        } else {
          toast.success(
            `${data.queued} email in coda (max ${data.daily_cap}/giorno)` +
              (perche ? ` · saltati: ${perche}` : ""),
            { duration: 8000 }
          );
        }
        await loadQueue();
      }
      if (action === "sample") {
        const { data } = await api.post("/leads/sample-quotes", { ids: selected });
        toast.success(
          `${data.created} preventivi di esempio pronti · ${data.already} già preparati`
        );
      }
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const patch = async (lead, updates) => {
    try {
      await api.patch(`/leads/${lead.id}`, updates);
      setData((prev) => ({
        ...prev,
        items: prev.items.map((l) => (l.id === lead.id ? { ...l, ...updates } : l)),
      }));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const openSample = async (lead) => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/sample-quote`);
      window.open(data.url, "_blank", "noopener");
      if (!data.already) toast.success("Preventivo di esempio pronto e inserito nel messaggio");
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const toClient = async (lead) => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/to-client`);
      toast.success(
        data.already
          ? `${data.name} è già tra i tuoi clienti`
          : `${data.name} è ora un tuo cliente`
      );
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const markFollowedUp = async (lead) => {
    await api.post(`/leads/${lead.id}/followup-done`);
    setFollowups((prev) => ({
      ...prev,
      items: prev.items.filter((l) => l.id !== lead.id),
      total: prev.total - 1,
    }));
  };

  const remove = async (lead) => {
    await api.delete(`/leads/${lead.id}`);
    setData((prev) => ({ ...prev, items: prev.items.filter((l) => l.id !== lead.id) }));
    toast.success("Lead eliminato");
  };

  const sendNow = async () => {
    setBusy("send");
    try {
      const { data } = await api.post("/leads/send-now");
      if (data.reason) toast.info(data.reason);
      else toast.success(`Inviate ${data.sent} email · ${data.failed} errori`);
      await loadQueue();
      await load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams(
      Object.entries(filters).filter(([k, v]) => v && k !== "q")
    ).toString();
    const token = localStorage.getItem("op_token");
    fetch(`${API}/leads/export.csv?${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `lead-operativo-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Esportazione non riuscita"));
  };

  const allSelected = data.items.length > 0 && selected.length === data.items.length;
  const toggleAll = () =>
    setSelected(allSelected ? [] : data.items.map((l) => l.id));
  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const waLink = (lead) =>
    `https://wa.me/39${(lead.telefono || "").replace(/\D/g, "").replace(/^39/, "")}?text=${encodeURIComponent(
      lead.messaggio_whatsapp || ""
    )}`;

  const shortDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }) : "";

  const isContacted = (lead) =>
    lead.email_status === "sent" ||
    ["inviato", "risposto"].includes(lead.whatsapp_status) ||
    ["contattato", "risposto", "chiuso"].includes(lead.stato);

  const contactBadges = (lead) => {
    const out = [];
    if (lead.email_status === "sent")
      out.push(["Email inviata " + shortDate(lead.email_sent_at), "bg-emerald-600 text-white"]);
    else if (lead.email_status === "pending")
      out.push(["Email in coda", "bg-amber-100 text-amber-800 border border-amber-200"]);
    else if (lead.email_status === "failed")
      out.push(["Email non riuscita", "bg-red-100 text-red-700 border border-red-200"]);
    if (["inviato", "risposto"].includes(lead.whatsapp_status))
      out.push([
        "WhatsApp " + shortDate(lead.wa_sent_at),
        "bg-emerald-100 text-emerald-800 border border-emerald-200",
      ]);
    if (lead.stato === "risposto")
      out.push(["Ha risposto", "bg-indigo-600 text-white"]);
    if (lead.stato === "chiuso")
      out.push(["Diventato cliente", "bg-indigo-600 text-white"]);
    if (!out.length && lead.stato === "contattato")
      out.push(["Contattato", "bg-slate-200 text-slate-700"]);
    return out;
  };

  const stat = (label, value, testid, tone = "text-slate-900") => (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-xl font-extrabold ${tone}`}>{value ?? 0}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="lead-finder-page">
      {sequence && (
        <WhatsAppSequence
          leads={sequence}
          onMark={(lead, status) => patch(lead, { whatsapp_status: status })}
          onClose={() => {
            setSequence(null);
            load();
            loadFollowups();
          }}
        />
      )}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-900">
            <Radar className="h-4 w-4 text-white" />
          </span>
          <div>
            <h2 className="text-base font-bold md:text-lg">Trova nuovi lead</h2>
            <p className="text-sm text-slate-500">
              Dati pubblici OpenStreetMap: nome, indirizzo, telefono e sito. Nessun duplicato.
            </p>
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-[auto_1fr_1fr_auto]">
          <div>
            <Label htmlFor="lf-ambito">Cerca per</Label>
            <Select
              id="lf-ambito"
              value={ambito}
              onChange={(v) => {
                setAmbito(v);
                setLuogo("");
              }}
            >
              <option value="regione">Regione</option>
              <option value="provincia">Provincia</option>
              <option value="comune">Comune</option>
            </Select>
          </div>
          <div>
            <Label htmlFor={ambito === "regione" ? "lf-regione" : "lf-luogo"}>
              {ambito === "regione" ? "Regione" : ambito === "provincia" ? "Provincia" : "Comune"}
            </Label>
            {ambito === "regione" ? (
              <Select id="lf-regione" value={regione} onChange={setRegione}>
                {(meta?.regioni || []).map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                id="lf-luogo"
                data-testid="lf-luogo"
                value={luogo}
                onChange={(e) => setLuogo(e.target.value)}
                placeholder={ambito === "comune" ? "Es. Milano" : "Es. Bergamo"}
                className="mt-1.5"
              />
            )}
          </div>
          <div>
            <Label htmlFor="lf-categoria">Categoria</Label>
            <Select id="lf-categoria" value={categoria} onChange={setCategoria}>
              {(meta?.categorie || []).map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                  {c.target ? " ★" : ""}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              data-testid="lf-search-btn"
              onClick={cerca}
              disabled={searching || (ambito !== "regione" && luogo.trim().length < 3)}
              className="w-full gap-2 bg-slate-900 text-white hover:bg-slate-800 md:w-auto"
            >
              {searching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              {searching ? "Ricerca in corso…" : "Cerca aziende"}
            </Button>
          </div>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            data-testid="lf-solo-contatti"
            checked={soloContatti}
            onChange={(e) => setSoloContatti(e.target.checked)}
          />
          Salva solo aziende con almeno un contatto (telefono, sito o email)
        </label>
        {searching && (
          <p className="mt-2 text-xs text-slate-500" data-testid="lf-job-msg">
            {jobMsg} La ricerca su una regione intera può richiedere qualche minuto: puoi
            restare su questa pagina.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        {stat("Totale", data.stats.all, "lf-stat-all")}
        {stat("HOT", data.stats.hot, "lf-stat-hot", "text-red-600")}
        {stat("WARM", data.stats.warm, "lf-stat-warm", "text-amber-600")}
        {stat("Con email", data.stats.with_email, "lf-stat-email")}
        {stat("Esche viste", data.stats.sample_seen, "lf-stat-sample", "text-emerald-600")}
        {stat("Contattati", data.stats.contattati, "lf-stat-contacted", "text-emerald-600")}
        {stat("Mai contattati", data.stats.mai_contattati, "lf-stat-never")}
        {stat(
          `Email oggi (max ${meta?.daily_cap ?? 20})`,
          queue?.sent_today,
          "lf-stat-today"
        )}
      </div>

      {followups.total > 0 && (
        <div
          className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5"
          data-testid="lf-followups-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-base font-bold md:text-lg">
                Da richiamare su WhatsApp ({followups.total})
              </h2>
              <p className="text-sm text-slate-600">
                Gli hai scritto più di {followups.days} giorni fa e non hanno risposto. Messaggio
                di richiamo già pronto.
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {followups.items.slice(0, 20).map((lead) => (
              <div
                key={lead.id}
                data-testid={`lf-followup-${lead.id}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-100 bg-white px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{lead.nome_azienda}</p>
                  <p className="text-xs text-slate-500">
                    {lead.telefono} · {lead.citta || lead.regione} · {lead.giorni_attesa} giorni di
                    silenzio
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`https://wa.me/39${(lead.telefono || "")
                      .replace(/\D/g, "")
                      .replace(/^39/, "")}?text=${encodeURIComponent(lead.messaggio_richiamo)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`lf-followup-open-${lead.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-500"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> Apri richiamo
                  </a>
                  <button
                    type="button"
                    data-testid={`lf-followup-done-${lead.id}`}
                    onClick={() => markFollowedUp(lead)}
                    className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                  >
                    Fatto
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {recent.total > 0 && (
        <div
          className="rounded-xl border border-slate-200 bg-white p-5"
          data-testid="lf-recent-card"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-base font-bold md:text-lg">
              Chi hai già contattato ({recent.total})
            </h2>
            <button
              type="button"
              data-testid="lf-recent-filter"
              onClick={() => setFilters({ ...filters, contatto: "contattati" })}
              className="text-xs font-semibold text-indigo-600 hover:underline"
            >
              Mostrali tutti nella tabella
            </button>
          </div>
          <p className="text-sm text-slate-500">
            Dal più recente. Questi non vanno ricontattati: se non rispondono li ritrovi nei
            richiami dopo 3 giorni.
          </p>
          <div className="mt-4 divide-y divide-slate-100">
            {recent.items.map((lead) => (
              <div
                key={lead.id}
                data-testid={`lf-recent-${lead.id}`}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{lead.nome_azienda}</p>
                  <p className="text-xs text-slate-500">
                    {lead.citta || lead.regione} · {lead.email || lead.telefono || "—"}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {contactBadges(lead).map(([label, style]) => (
                    <span
                      key={label}
                      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold ${style}`}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-4 xl:grid-cols-7">
          <div>
            <Label htmlFor="lf-f-regione">Zona</Label>
            <Select
              id="lf-f-regione"
              value={filters.regione}
              onChange={(v) => setFilters({ ...filters, regione: v })}
            >
              <option value="">Tutte</option>
              {(meta?.zone || meta?.regioni || []).map((r) => (
                <option key={r}>{r}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="lf-f-categoria">Categoria</Label>
            <Select
              id="lf-f-categoria"
              value={filters.categoria}
              onChange={(v) => setFilters({ ...filters, categoria: v })}
            >
              <option value="">Tutte</option>
              {(meta?.categorie || []).map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="lf-f-score">Score</Label>
            <Select
              id="lf-f-score"
              value={filters.score}
              onChange={(v) => setFilters({ ...filters, score: v })}
            >
              <option value="">Tutti</option>
              <option value="HOT">HOT</option>
              <option value="WARM">WARM</option>
              <option value="COLD">COLD</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="lf-f-stato">Stato</Label>
            <Select
              id="lf-f-stato"
              value={filters.stato}
              onChange={(v) => setFilters({ ...filters, stato: v })}
            >
              <option value="">Tutti</option>
              {(meta?.stati || []).map((s) => (
                <option key={s} value={s}>
                  {STATO_LABEL[s] || s}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="lf-f-whatsapp">WhatsApp</Label>
            <Select
              id="lf-f-whatsapp"
              value={filters.whatsapp}
              onChange={(v) => setFilters({ ...filters, whatsapp: v })}
            >
              <option value="">Tutti</option>
              <option value="probabile">Cellulare</option>
              <option value="inviato">Messaggio inviato</option>
              <option value="risposto">Ha risposto</option>
              <option value="no_whatsapp">Senza WhatsApp</option>
              <option value="sconosciuto">Fisso / ignoto</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="lf-f-contatto">Contatto</Label>
            <Select
              id="lf-f-contatto"
              value={filters.contatto}
              onChange={(v) => setFilters({ ...filters, contatto: v })}
            >
              <option value="">Tutti</option>
              <option value="mai">Mai contattati</option>
              <option value="contattati">Già contattati</option>
              <option value="email_inviata">Email inviata</option>
              <option value="email_coda">Email in coda</option>
              <option value="wa_inviato">WhatsApp inviato</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="lf-f-q">Cerca</Label>
            <Input
              id="lf-f-q"
              data-testid="lf-f-q"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Nome, città, telefono"
              className="mt-1.5"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
          <span className="text-sm font-semibold text-slate-600" data-testid="lf-selected-count">
            {selected.length} selezionati
          </span>
          <Button
            data-testid="lf-enrich-btn"
            variant="outline"
            onClick={() => bulk("enrich")}
            disabled={busy === "enrich"}
            className="gap-2"
          >
            {busy === "enrich" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Globe className="h-4 w-4" />
            )}
            Trova email dal sito
          </Button>
          <Button
            data-testid="lf-wa-seq-btn"
            onClick={() => {
              const chosen = data.items.filter(
                (l) => selected.includes(l.id) && l.telefono && l.whatsapp_status !== "no_whatsapp"
              );
              if (!chosen.length)
                return toast.error("Seleziona lead con un numero di telefono");
              if (chosen.length > 15)
                toast.info("Ne hai selezionati molti: fermati a 15 al giorno per sicurezza");
              setSequence(chosen.slice(0, 25));
            }}
            className="gap-2 bg-emerald-600 text-white hover:bg-emerald-500"
          >
            <MessageCircle className="h-4 w-4" /> Sequenza WhatsApp
          </Button>
          <Button
            data-testid="lf-whatsapp-btn"
            variant="outline"
            onClick={() => bulk("whatsapp")}
            disabled={busy === "whatsapp"}
            className="gap-2"
          >
            <MessageCircle className="h-4 w-4" /> Verifica cellulari
          </Button>
          <div className="flex items-center gap-2">
            <Button
              data-testid="lf-preview-btn"
              variant="outline"
              onClick={() => window.open(`${API}/leads/email-preview`, "_blank", "noopener")}
              className="gap-2"
            >
              <Eye className="h-4 w-4" /> Vedi l'email
            </Button>
            <Button
              data-testid="lf-queue-btn"
              onClick={() => bulk("queue")}
              disabled={busy === "queue"}
              className="gap-2 bg-indigo-600 text-white hover:bg-indigo-500"
            >
              <Mail className="h-4 w-4" /> Metti in coda email
            </Button>
          </div>
          <Button
            data-testid="lf-sample-btn"
            variant="outline"
            onClick={() => bulk("sample")}
            disabled={busy === "sample"}
            className="gap-2"
          >
            <FileText className="h-4 w-4" /> Preventivi esca
          </Button>
          <Button
            data-testid="lf-export-btn"
            variant="outline"
            onClick={exportCsv}
            className="gap-2"
          >
            <Download className="h-4 w-4" /> Esporta CSV
          </Button>
          <Button data-testid="lf-reload-btn" variant="ghost" onClick={load} className="gap-2">
            <RefreshCw className="h-4 w-4" /> Aggiorna
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[1000px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="p-3">
                <input
                  type="checkbox"
                  data-testid="lf-select-all"
                  checked={allSelected}
                  onChange={toggleAll}
                />
              </th>
              <th className="p-3">Azienda</th>
              <th className="p-3">Zona</th>
              <th className="p-3">Contatti</th>
              <th className="p-3">WA</th>
              <th className="p-3">Contattato</th>
              <th className="p-3">Score</th>
              <th className="p-3">Stato</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody data-testid="lf-table-body">
            {data.items.map((lead) => (
              <tr
                key={lead.id}
                className={`border-t border-slate-100 hover:bg-slate-50 ${
                  isContacted(lead)
                    ? "border-l-4 border-l-emerald-500 bg-emerald-50/40"
                    : lead.email_status === "pending"
                      ? "border-l-4 border-l-amber-400 bg-amber-50/40"
                      : ""
                }`}
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    data-testid={`lf-row-check-${lead.id}`}
                    checked={selected.includes(lead.id)}
                    onChange={() => toggle(lead.id)}
                  />
                </td>
                <td className="p-3">
                  <p className="font-semibold">{lead.nome_azienda}</p>
                  <p className="text-xs text-slate-500">
                    {catLabel[lead.categoria] || lead.categoria}
                  </p>
                  {lead.sample_token && (
                    <p className="text-xs font-semibold text-emerald-700">
                      esca pronta
                      {lead.sample_views ? ` · vista ${lead.sample_views}×` : ""}
                    </p>
                  )}
                </td>
                <td className="p-3 text-xs text-slate-600">
                  <p>{lead.citta || "—"}</p>
                  <p className="text-slate-400">{lead.regione}</p>
                </td>
                <td className="p-3 text-xs">
                  <p>{lead.telefono || "—"}</p>
                  {lead.email ? (
                    <p className="text-emerald-700">{lead.email}</p>
                  ) : (
                    <p className="text-slate-400">nessuna email</p>
                  )}
                  {lead.sito_web && (
                    <a
                      href={lead.sito_web}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      sito
                    </a>
                  )}
                </td>
                <td className="p-3">
                  {lead.telefono && lead.whatsapp_status !== "no_whatsapp" ? (
                    <div className="flex items-center gap-1">
                      {lead.whatsapp_status === "probabile" ||
                      lead.whatsapp_status === "inviato" ||
                      lead.whatsapp_status === "risposto" ? (
                        <MessageCircle className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <MessageCircle className="h-4 w-4 text-slate-300" />
                      )}
                      <a
                        href={waLink(lead)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`lf-wa-open-${lead.id}`}
                        onClick={() => patch(lead, { whatsapp_status: "inviato" })}
                        className="text-xs font-semibold text-emerald-700 hover:underline"
                      >
                        Apri chat
                      </a>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
                <td className="p-3" data-testid={`lf-contact-${lead.id}`}>
                  {contactBadges(lead).length ? (
                    <div className="flex flex-col items-start gap-1">
                      {contactBadges(lead).map(([label, style]) => (
                        <span
                          key={label}
                          className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold ${style}`}
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">mai contattato</span>
                  )}
                </td>
                <td className="p-3">
                  <span
                    className={`rounded-full border px-2 py-0.5 text-xs font-bold ${SCORE_STYLE[lead.score]}`}
                    data-testid={`lf-score-${lead.id}`}
                  >
                    {lead.score}
                  </span>
                </td>
                <td className="p-3">
                  <select
                    data-testid={`lf-stato-${lead.id}`}
                    value={lead.stato}
                    onChange={(e) => patch(lead, { stato: e.target.value })}
                    className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs"
                  >
                    {Object.entries(STATO_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="p-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      data-testid={`lf-sample-${lead.id}`}
                      onClick={() => openSample(lead)}
                      title="Preventivo di esempio per questa azienda"
                      className={`transition-colors ${
                        lead.sample_token
                          ? "text-emerald-600 hover:text-emerald-500"
                          : "text-slate-400 hover:text-emerald-600"
                      }`}
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      data-testid={`lf-to-client-${lead.id}`}
                      onClick={() => toClient(lead)}
                      title="Trasforma in cliente"
                      className="text-slate-400 transition-colors hover:text-indigo-600"
                    >
                      <UserPlus className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      data-testid={`lf-delete-${lead.id}`}
                      onClick={() => remove(lead)}
                      className="text-slate-400 transition-colors hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!data.items.length && (
              <tr>
                <td colSpan={8} className="p-10 text-center text-sm text-slate-500">
                  Nessun lead. Scegli regione e categoria, poi premi “Cerca aziende”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="lf-queue-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-bold md:text-lg">Coda email fredde</h2>
            <p className="text-sm text-slate-500">
              Un'unica email per tutti i professionisti, con il preventivo di esempio in allegato.
              Invio automatico ogni giorno alle 09:00, massimo {queue?.daily_cap ?? 20} email.
              Ogni email contiene il link di disiscrizione.
            </p>
          </div>
          <Button
            data-testid="lf-test-email-btn"
            variant="outline"
            onClick={async () => {
              const to = window.prompt(
                "A quale indirizzo mando l'email di prova?",
                "antonella-salvia@alice.it"
              );
              if (!to) return;
              try {
                const { data } = await api.post("/leads/test-email", { email: to });
                toast.success(
                  `Email di prova inviata a ${data.sent_to} (PDF ${data.attachment_kb} KB)`
                );
              } catch (err) {
                toast.error(apiError(err.response?.data?.detail));
              }
            }}
            className="gap-2"
          >
            <Mail className="h-4 w-4" /> Prova su di me
          </Button>
          <Button
            data-testid="lf-send-now-btn"
            onClick={sendNow}
            disabled={busy === "send"}
            className="gap-2 bg-emerald-600 text-white hover:bg-emerald-500"
          >
            {busy === "send" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Invia ora (rispetta il limite)
          </Button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
          {stat("In coda", queue?.pending, "lf-q-pending")}
          {stat("Inviate oggi", queue?.sent_today, "lf-q-today")}
          {stat("Inviate totali", queue?.sent_total, "lf-q-total")}
          {stat("Errori", queue?.failed, "lf-q-failed", "text-red-600")}
          {stat("Disiscritti", queue?.opted_out, "lf-q-optout")}
        </div>
        {!meta?.email_configured && (
          <p className="mt-3 rounded-md bg-amber-50 p-3 text-xs font-semibold text-amber-800">
            Resend non è configurato: le email restano in coda.
          </p>
        )}
        <div className="mt-4 max-h-64 overflow-y-auto">
          {(queue?.items || []).slice(0, 50).map((row) => (
            <div
              key={row.id}
              className="flex items-center justify-between border-b border-slate-100 py-2 text-xs"
            >
              <span className="font-semibold">{row.nome_azienda}</span>
              <span className="text-slate-500">{row.email}</span>
              <span
                className={
                  row.status === "sent"
                    ? "text-emerald-600"
                    : row.status === "failed"
                      ? "text-red-600"
                      : "text-slate-500"
                }
              >
                {row.status}
              </span>
              {row.status === "pending" && (
                <button
                  data-testid={`lf-dequeue-${row.id}`}
                  onClick={async () => {
                    await api.delete(`/leads/email-queue/${row.id}`);
                    loadQueue();
                    load();
                  }}
                  className="text-slate-400 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeadFinder;
